import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download,
  Close,
  PictureAsPdf,
  Description,
  TableChart,
  Image,
  Archive,
  Code,
  Email,
  InsertDriveFile,
} from '@mui/icons-material';
import type { RfqFileRow } from '../../../api/rfqFiles';
import { formatSingaporeDateTime } from '../../../utils/timezone';
import { formatFileSize } from '../../../utils/format';

/** An rfq_files row whose object is in Storage. */
export type AttachmentFile = RfqFileRow & { storage_path: string; storage_bucket: string };

const isImageAttachment = (attachment: AttachmentFile): boolean => {
  if (!attachment) return false;
  const ct = attachment.content_type?.toLowerCase() || '';
  if (ct.startsWith('image/')) return true;
  if (attachment.file_type === 'IMAGE') return true;
  const lowerName = (attachment.filename || '').toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].some(ext => lowerName.endsWith(ext));
};

const fileIcon = (fileType: string) => {
  switch (fileType) {
    case 'EXCEL':
      return <TableChart color="success" />;
    case 'PDF':
      return <PictureAsPdf color="error" />;
    case 'WORD':
    case 'DOCUMENT':
      return <Description color="primary" />;
    case 'POWERPOINT':
      return <Description color="warning" />;
    case 'IMAGE':
      return <Image color="info" />;
    case 'ARCHIVE':
      return <Archive color="secondary" />;
    case 'TEXT':
      return <Code color="action" />;
    case 'EMAIL':
      return <Email color="primary" />;
    default:
      return <InsertDriveFile color="action" />;
  }
};

const labelFor = (fileType: string) => {
  switch (fileType) {
    case 'EXCEL':
      return 'Excel';
    case 'PDF':
      return 'PDF';
    case 'WORD':
      return 'Word';
    case 'DOCUMENT':
      return 'Document';
    case 'POWERPOINT':
      return 'PowerPoint';
    case 'IMAGE':
      return 'Image';
    case 'ARCHIVE':
      return 'Archive';
    case 'TEXT':
      return 'Text';
    case 'EMAIL':
      return 'Email';
    case 'WEB':
      return 'Web';
    case 'CAD':
      return 'CAD';
    default:
      return 'Other';
  }
};

const colorFor = (fileType: string): "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (fileType) {
    case 'EXCEL':
      return 'success';
    case 'PDF':
      return 'error';
    case 'WORD':
    case 'DOCUMENT':
      return 'primary';
    case 'POWERPOINT':
      return 'warning';
    case 'IMAGE':
      return 'info';
    default:
      return 'secondary';
  }
};

interface AttachmentCardProps {
  attachment: AttachmentFile;
  /** The image's signed URL once its preview has been loaded. */
  imageUrl: string | undefined;
  downloading: boolean;
  onPreview: () => void;
  onDownload: () => void;
}

/** One attachment: type, image preview, name, size and date, View and Download. */
export default function AttachmentCard({ attachment, imageUrl, downloading, onPreview, onDownload }: AttachmentCardProps) {
  const isImage = isImageAttachment(attachment);
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 120ms',
        '&:hover': { borderColor: 'grey.400' },
      }}
    >
      <CardContent sx={{ pb: 1, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {fileIcon(attachment.file_type)}
          <Chip
            label={labelFor(attachment.file_type)}
            size="small"
            color={colorFor(attachment.file_type)}
          />
        </Box>

        {isImage && (
          <Box sx={{
            width: '100%',
            height: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'grey.50',
            border: 1,
            borderColor: 'divider'
          }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={attachment.filename}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
                onClick={onPreview}
              />
            ) : (
              <Button size="small" onClick={onPreview}>
                Load Preview
              </Button>
            )}
          </Box>
        )}

        <Tooltip title={attachment.filename}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
              mb: 0.5
            }}
          >
            {attachment.filename}
          </Typography>
        </Tooltip>

        {/* A null file_size or created_at renders exactly as it did when the row was untyped. */}
        <Typography variant="caption" color="text.secondary" display="block">
          {formatFileSize(attachment.file_size as number, 2)}
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block">
          {formatSingaporeDateTime(attachment.created_at as string)}
        </Typography>
      </CardContent>

      <CardActions sx={{ borderTop: 1, borderColor: 'divider', px: 1, py: 0.5 }}>
        {isImage && (
          <Button
            size="small"
            onClick={onPreview}
            fullWidth
          >
            View
          </Button>
        )}
        <Button
          size="small"
          startIcon={
            downloading ? (
              <CircularProgress size={16} />
            ) : (
              <Download />
            )
          }
          onClick={onDownload}
          disabled={downloading}
          fullWidth
        >
          {downloading ? 'Downloading...' : 'Download'}
        </Button>
      </CardActions>
    </Card>
  );
}

/** An image attachment, full size. */
export function ImagePreviewDialog({ url, title, onClose, fullScreen }: { url: string | null; title: string; onClose: () => void; fullScreen: boolean }) {
  return (
    <Dialog open={!!url} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle component="div" sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography component="h2" noWrap sx={{ fontSize: '1.125rem', fontWeight: 600, minWidth: 0 }}>
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close image preview">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {url && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={url}
              alt={title}
              style={{ maxWidth: '100%', maxHeight: fullScreen ? 'calc(100vh - 160px)' : '75vh', objectFit: 'contain' }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
